import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { withAppHOC } from "../../hoc";
import { AppContext } from "../../context";
import Loader from "../../components/Loader/Loader";
import ProductCard from "../../components/Cards/ProductCart";
import CartProductCard from "../../components/Cards/CartProductCard";
import { StyledPageHeading, StyledPageBody } from "../../components/Styled";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";

const StyledProductsContainer = styled.section`
  width: 100%;
  height: auto;
  margin: 0px;
  padding: 0px;
  display: flex;
  column-gap: 20px;
  flex-wrap: wrap;
`;

const StyledProductsList = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  row-gap: 20px;
`;

const StyledSearchContainer = styled.section`
  input {
    font-size: 14px;
    color: #000000;
    padding: 15px;
    border: 1px solid #d8d8d8;
    border-radius: 5px;
    margin: 0px;
    width: 100%;
    max-width: 400px;
    outline-color: #d88100;
  }
`;

const StyledCartContainer = styled.section`
  width: 100%;
  max-width: 500px;
  border: 1px solid #d8d8d8;
  border-radius: 10px;
  height: fit-content;
`;

const StyledCartHeading = styled.section`
  text-align: center;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  background-color: #f3f3f3;
  h3 {
    color: #000000;
    font-size: 20px;
    font-weight: 500;
    margin: 0px;
    padding: 20px 0px;
    border-bottom: 1px solid #d8d8d8;
  }
`;

const StyledCartBody = styled.section`
  padding: 20px 5px 20px 20px;
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  max-height: 400px;
  overflow: scroll;
`;

const StyledEmptyCart = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  p {
    font-size: 16px;
    font-weight: normal;
    color: #cfcfcf;
    padding: 50px 0px 30px 0px;
    margin: 0px;
  }
`;

const StyledCartFooter = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  border-top: 1px solid #d8d8d8;
`;

const StyledContinueButton = styled.button`
  font-size: 16px;
  font-weight: normal;
  color: #ffffff;
  background-color: #ff9800;
  padding: 15px;
  margin: 0px;
  border-radius: 5px;
  border: 0px;
  cursor: pointer;
  outline: 0px;
  transition: background-color 0.5s ease;
  &:hover {
    background-color: #d88100;
    transition: background-color 0.5s ease;
  }
  &:disabled {
    background-color: #cfcfcf;
    color: #9a9a9a;
    cursor: not-allowed;
  }
`;

const StyledClearButton = styled.button`
  font-size: 14px;
  font-weight: normal;
  border: 0px;
  background-color: transparent;
  color: #767676;
  padding: 0px;
  margin: 0px;
  outline: 0px;
  cursor: pointer;
  transition: color 0.5s ease;
  &:hover {
    color: #000000;
    transition: color 0.5s ease;
  }
  &:disabled {
    color: #cfcfcf;
    cursor: not-allowed;
  }
`;

const Products = () => {
  const { isLoggedIn } = useContext(AppContext);
  const [state, setState] = useState({
    isLoading: false,
    apiResponseStatus: "",
    apiResponseMessage: "",
    searchQuery: "",
    products: [],
    cart: [],
  });

  const setStateData = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const fetchProducts = async () => {
    try {
      setStateData("isLoading", true);
      const response = await API.get("/products");
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("products", data);
      } else {
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", message);
      }
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  const fetchCart = async () => {
    if (isLoggedIn) {
      try {
        setStateData("isLoading", true);
        const response = await API.get("/cart");
        const { status, message, data } = response?.data;
        if (status === RESPONSE_STATUSES.SUCCESS) {
          setStateData("cart", data);
        } else {
          setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
          setStateData("apiResponseMessage", message);
        }
      } catch (error) {
        console.error(error);
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", error.message);
      } finally {
        setStateData("isLoading", false);
      }
    }
  };

  const addProduct = async (productsId) => {
    try {
      setStateData("isLoading", true);
      const response = await API.post("/cart", { productsId });
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("cart", data);
      } else {
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", message);
      }
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  const removeProduct = async (productsId) => {
    try {
      setStateData("isLoading", true);
      const response = await API.delete(`/cart/remove/${productsId}`);
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("cart", data);
      } else {
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", message);
      }
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  const clearCart = async () => {
    try {
      setStateData("isLoading", true);
      const response = await API.delete(`/cart/clear`);
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("cart", data);
      } else {
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", message);
      }
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  const incrementQuantity = async (productsId) => {
    try {
      setStateData("isLoading", true);
      const response = await API.put(`/cart/${productsId}`, { process: "I" });
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("cart", data);
      } else {
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", message);
      }
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  const decrementQuantity = async (productsId) => {
    try {
      setStateData("isLoading", true);
      const response = await API.put(`/cart/${productsId}`, { process: "D" });
      const { status, message, data } = response?.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setStateData("cart", data);
      } else {
        setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
        setStateData("apiResponseMessage", message);
      }
    } catch (error) {
      console.error(error);
      setStateData("apiResponseStatus", RESPONSE_STATUSES.FAILED);
      setStateData("apiResponseMessage", error.message);
    } finally {
      setStateData("isLoading", false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCart();
  }, []);

  const finalProducts = state.products.filter(({ productTitle }) =>
    state.searchQuery
      ? productTitle
          ?.toString()
          ?.toLowerCase()
          .includes(state.searchQuery?.toString()?.toLowerCase())
      : true
  );

  return (
    <>
      <StyledPageHeading>
        <h1>Our Products</h1>
      </StyledPageHeading>
      <StyledPageBody>
        <Loader isLoading={state.isLoading} />
        <StyledProductsContainer>
          <StyledProductsList>
            <StyledSearchContainer>
              <input
                type="search"
                placeholder="Search product"
                value={state.searchQuery}
                onChange={(e) => setStateData("searchQuery", e.target.value)}
              />
            </StyledSearchContainer>
            {finalProducts.map((item, index) => {
              return (
                <ProductCard
                  {...item}
                  key={index}
                  alreadyInCart={state.cart.some(
                    (cItem) => cItem.id === item.id
                  )}
                  addProduct={addProduct}
                  removeProduct={removeProduct}
                />
              );
            })}
          </StyledProductsList>
          <StyledCartContainer>
            <StyledCartHeading>
              <h3>Your Shopping Cart</h3>
            </StyledCartHeading>
            <StyledCartBody>
              {state.cart.length === 0 && (
                <StyledEmptyCart>
                  <p>Your cart is empty</p>
                </StyledEmptyCart>
              )}
              {state.cart.map((item, index) => (
                <CartProductCard
                  key={index}
                  {...item}
                  incrementQuantity={incrementQuantity}
                  decrementQuantity={decrementQuantity}
                  removeProduct={removeProduct}
                />
              ))}
            </StyledCartBody>
            <StyledCartFooter>
              <StyledContinueButton disabled={state.cart.length === 0}>
                Continue
              </StyledContinueButton>
              <StyledClearButton
                onClick={clearCart}
                disabled={state.cart.length === 0}
              >
                Clear
              </StyledClearButton>
            </StyledCartFooter>
          </StyledCartContainer>
        </StyledProductsContainer>
      </StyledPageBody>
    </>
  );
};

export default withAppHOC(Products);
